import React, { useEffect, useState } from 'react';
import {
  Button,
  Upload,
  Progress,
  Spin,
  notification,
} from 'antd';
import type { ButtonProps } from 'antd';
import {
  UploadFile, UploadProps, RcFile, UploadFileStatus,
} from 'antd/lib/upload/interface';
import Icon, { InfoCircleOutlined } from '@ant-design/icons';
import { Tooltip } from 'components/Tooltip';
import classnames from 'classnames';

import MaterialsSvg from './materials.svg';
import cn from './styles.module.scss';

const { Dragger } = Upload;

export type FileUploadProps = Omit<UploadProps, 'onChange'> & {
  defaultFiles?: any[],
  onRequest: (file: UploadFile, setProgress: (progress: number) => void) => Promise<void>,
  onChange?: (file: UploadFile[]) => void;
  supportedFileTypes?: string[];
  uploadAnotherButtonProps?: ButtonProps;
  labels?: {
    dragDropTitle?: string;
    uploadButton?: string;
    uploadAnotherButton?: string;
  };
};

export const FileUpload = ({
  defaultFiles = [],
  onRequest,
  onChange,
  supportedFileTypes,
  beforeUpload,
  uploadAnotherButtonProps,
  labels = {},
  ...props
}: FileUploadProps) => {
  const mergedLabels = {
    dragDropTitle: 'Drag & Drop File Here to Upload',
    uploadButton: 'Upload File',
    uploadAnotherButton: 'Upload another file',
    ...labels,
  };
  const [files, setFiles] = useState(defaultFiles?.map((defaultFile) => ({
    ...defaultFile,
    percent: 100,
    status: 'done',
  })));

  const changeFiles = (newFiles: UploadFile[]) => {
    setFiles(newFiles);
  };

  const handleRequest = async ({ file: fileToUpload }: { file: UploadFile }) => {
    let error = '';
    let status: UploadFileStatus = 'done';

    try {
      setFiles((prevFiles) => prevFiles.map((prevFile) => {
        if (prevFile.uid !== fileToUpload.uid) {
          return prevFile;
        }
        return {
          ...prevFile,
          ...fileToUpload,
          status: 'uploading',
        };
      }));

      await onRequest(fileToUpload, (percent) => {
        setFiles((prevFiles) => prevFiles.map((prevFile) => {
          if (prevFile.uid !== fileToUpload.uid) {
            return prevFile;
          }
          return {
            ...prevFile,
            ...fileToUpload,
            percent,
          };
        }));
      });
    } catch (e: any) {
      status = 'error';
      error = e.message;
    } finally {
      setFiles((prevFiles) => prevFiles.map((prevFile) => {
        if (prevFile.uid !== fileToUpload.uid) {
          return prevFile;
        }
        return {
          ...prevFile,
          ...fileToUpload,
          status,
          error,
        };
      }));
    }
  };

  const handleOnChange = ({ fileList }: { fileList: UploadFile[] }) => {
    changeFiles(fileList);
  };

  const handleRemove = () => {
    changeFiles([]);
  };

  const handleBeforeUpload = async (file: RcFile, fileList: RcFile[]) => {
    if (supportedFileTypes && !supportedFileTypes.includes(file.type)) {
      notification.error({
        message: 'Unsupported format',
        description: `${file.name} has wrong format. Please use: ${supportedFileTypes.join(', ')}.`,
      });
      return Upload.LIST_IGNORE;
    }

    if (beforeUpload) {
      return beforeUpload(file, fileList);
    }

    return true;
  };

  useEffect(() => {
    if (onChange) {
      onChange(files);
    }
  }, [onChange, files]);

  return (
    <div className={cn.dragger}>
      <div className={cn.files}>
        {files.map((file: UploadFile) => {
          const fileProgress = file.percent || 0;
          const fileError = file.error;

          return (
            <div key={file.uid} className={classnames(cn.progress, { [cn.error]: fileError })}>
              <div className={cn.infoContainer}>
                <div className={cn.info}>
                  <div className={cn.nameContainer}>
                    <div className={cn.name}>{file.name}</div>
                    {fileError && (
                      <Tooltip title={fileError}>
                        <InfoCircleOutlined />
                      </Tooltip>
                    )}
                  </div>
                  {!fileError && (
                    <div className={cn.progressInfo}>
                      {`${fileProgress}% ${fileProgress !== 100 ? 'Uploading...' : ''}`}
                    </div>
                  )}
                </div>
                {file.status === 'uploading' && <Spin className={cn.spinner} />}
              </div>
              <Progress
                percent={fileProgress}
                showInfo={false}
                status={fileError ? 'exception' : undefined}
              />
            </div>
          );
        })}
      </div>
      {!!files?.length && (
        <Button
          color="primary"
          variant="outlined"
          className={cn.otherFilesButton}
          onClick={handleRemove}
          {...uploadAnotherButtonProps}
        >
          {mergedLabels.uploadAnotherButton}
        </Button>
      )}
      <div className={classnames({ [cn.hidden]: !!files.length })}>
        <Dragger
          customRequest={handleRequest as any}
          onChange={handleOnChange}
          fileList={files}
          showUploadList={false}
          beforeUpload={handleBeforeUpload}
          {...props}
        >
          <Icon component={MaterialsSvg} />
          <div className={cn.title}>{mergedLabels.dragDropTitle}</div>
          <div className={cn.button}>{mergedLabels.uploadButton}</div>
        </Dragger>
      </div>
    </div>
  );
};
